import { useEffect, useState } from "react";
import { api, formatApiError, API_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Download, Upload, Pencil, Trash2, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { usePager, Pager } from "@/components/Pager";

const CATEGORIES = ["Tube", "Hose", "Profile", "O'Rings", "Cord", "Strip", "Tri-Clamp"];
const unitFor = (cat) => (cat === "O'Rings" || cat === "Tri-Clamp" ? "Pieces" : "Meter");

const empty = {
  product_code: "", description: "", category: "Tube", unit: "Meter",
  price: 0, gst: 18, hsn: "", specifications: "", stock: 0, min_stock: 0,
};

export default function Products() {
  const [list, setList] = useState([]);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkHsn = async () => {
    setBulkBusy(true);
    const tid = toast.loading("Finding HSN codes for products missing one…");
    try {
      const r = await api.post("/products/ai-hsn/bulk", {}, { timeout: 180000 });
      const { filled, remaining, total_missing } = r.data;
      if (!total_missing) toast.success("All products already have an HSN code", { id: tid });
      else toast.success(`HSN filled for ${filled.length} product(s)${remaining ? ` — ${remaining} left, click again to continue` : ""}`, { id: tid, duration: 9000 });
      load();
    } catch (e) { toast.error(formatApiError(e), { id: tid }); }
    setBulkBusy(false);
  };
  const [hsnBusy, setHsnBusy] = useState(false);
  const findHsn = async () => {
    if (!form.description.trim()) { toast.error("Enter the product description first"); return; }
    setHsnBusy(true);
    try {
      const r = await api.post("/products/ai-hsn", {
        description: form.description, category: form.category, specifications: form.specifications });
      setForm((f) => ({ ...f, hsn: r.data.hsn }));
      toast.success(`HSN ${r.data.hsn}${r.data.gst_rate ? ` (GST ${r.data.gst_rate}%)` : ""} — ${r.data.reason}`, { duration: 8000 });
    } catch (e) { toast.error(formatApiError(e)); }
    setHsnBusy(false);
  };
  const [addingCat, setAddingCat] = useState(false);
  const [newCat, setNewCat] = useState("");
  const allCats = [...new Set([...CATEGORIES, ...list.map((p) => p.category).filter(Boolean), ...(form.category ? [form.category] : [])])];

  const load = () => api.get("/products", { params: { q, category: cat === "all" ? "" : cat } })
                          .then((r) => setList(r.data));
  const pager = usePager(list);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, cat]);

  const [impOpen, setImpOpen] = useState(false);
  const [impBusy, setImpBusy] = useState(false);
  const [impPreview, setImpPreview] = useState(null);
  const [impRes, setImpRes] = useState({});

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/products/import/template", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = "product_import_template.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadPreview = async (file) => {
    if (!file) return;
    setImpBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/products/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImpPreview(r.data);
      setImpRes(Object.fromEntries(r.data.duplicates.map((d) => [d.existing_id, "keep"])));
    } catch (e) { toast.error(formatApiError(e)); }
    setImpBusy(false);
  };

  const commitImport = async () => {
    setImpBusy(true);
    try {
      const updates = impPreview.duplicates.filter((d) => impRes[d.existing_id] === "replace")
        .map((d) => ({ id: d.existing_id, data: d.incoming }));
      const r = await api.post("/products/import/commit", { new: impPreview.new, updates });
      toast.success(`Imported: ${r.data.created} created, ${r.data.updated} updated${r.data.skipped ? `, ${r.data.skipped} skipped` : ""}`);
      setImpOpen(false); setImpPreview(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    setImpBusy(false);
  };

  const save = async () => {
    try {
      const body = { ...form, price: +form.price || 0, gst: +form.gst, stock: +form.stock || 0, min_stock: +form.min_stock || 0 };
      if (editing) await api.put(`/products/${editing}`, body); else await api.post("/products", body);
      toast.success("Product saved");
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/products/${id}`); load(); };
  const edit = (p) => { setEditing(p.id); setForm({ ...empty, ...p }); setOpen(true); };

  return (
    <div className="space-y-4" data-testid="products-page">
      <PageHeader title="Product Master" subtitle="Silicone extrusions catalog" />
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search code, description…" value={q} onChange={(e) => setQ(e.target.value)}
               className="max-w-md" data-testid="p-search" />
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-52" data-testid="p-cat"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {[...new Set([...CATEGORIES, ...list.map((p) => p.category).filter(Boolean)])].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <a href={`${API_URL}/products/export/excel`} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-300 rounded-md hover:bg-slate-50 text-slate-700">
          <Download size={14} /> Excel
        </a>
        <Button variant="outline" onClick={() => setImpOpen(true)} className="border-slate-300 text-slate-700 h-9" data-testid="product-import">
          <Upload size={14} className="mr-1.5" /> Import
        </Button>
        <Button variant="outline" onClick={bulkHsn} disabled={bulkBusy}
                className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 h-9" data-testid="p-bulk-hsn">
          <Wand2 size={14} className="mr-1.5" /> {bulkBusy ? "Filling HSN…" : "AI Fill Missing HSN"}
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="p-new"
                    className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">
              <Plus size={14} className="mr-1" /> New Product
            </Button>
          </DialogTrigger>
          <DialogContent className="w-[96vw] sm:max-w-[96vw] h-[94vh] max-h-[94vh] overflow-y-auto content-start lg:left-[calc(16rem+10px)] lg:translate-x-0 lg:top-[10px] lg:translate-y-0 lg:w-[calc(100vw-16rem-20px)] lg:max-w-[calc(100vw-16rem-20px)] lg:h-[calc(100vh-20px)] lg:max-h-[calc(100vh-20px)] lg:rounded-xl shadow-2xl dialog-slide-right">
            <DialogHeader><DialogTitle>{editing ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <F label="Code *" v={form.product_code} on={(v) => setForm({ ...form, product_code: v })} tid="p-code" />
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Category</Label>
                  <button type="button" onClick={() => { setAddingCat(!addingCat); setNewCat(""); }}
                          className="text-[11px] text-blue-600 hover:text-blue-800 font-medium" data-testid="p-add-category">
                    {addingCat ? "Cancel" : "+ Add new"}
                  </button>
                </div>
                {addingCat ? (
                  <div className="flex gap-1.5 mt-1.5">
                    <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name"
                           className="h-9" data-testid="p-new-category-input" autoFocus />
                    <Button type="button" size="sm" className="h-9 bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="p-new-category-save"
                            onClick={() => { const c = newCat.trim(); if (!c) return; setForm({ ...form, category: c, unit: unitFor(c) }); setAddingCat(false); }}>
                      Add
                    </Button>
                  </div>
                ) : (
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, unit: unitFor(v) })}>
                  <SelectTrigger className="mt-1.5" data-testid="p-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{allCats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                )}
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest text-slate-500">Product *</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                       placeholder="e.g. Platinum Cured Silicone Tube" className="mt-1.5" data-testid="p-desc" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Unit</Label>
                <Input value={form.unit} readOnly className="mt-1.5 bg-slate-50 text-slate-600" data-testid="p-unit" />
              </div>
              <F label="GST %" v={form.gst} on={(v) => setForm({ ...form, gst: v })} type="number" />
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">HSN Code</Label>
                  <button type="button" onClick={findHsn} disabled={hsnBusy}
                          className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                          data-testid="p-ai-hsn">
                    <Sparkles size={11} /> {hsnBusy ? "Finding…" : "AI Find HSN"}
                  </button>
                </div>
                <Input value={form.hsn} onChange={(e) => setForm({ ...form, hsn: e.target.value.replace(/\D/g, "").slice(0, 8) })}
                       placeholder="e.g. 40091100" className="mt-1.5" data-testid="p-hsn" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest text-slate-500">Specifications</Label>
                <Textarea value={form.specifications} onChange={(e) => setForm({ ...form, specifications: e.target.value })}
                          className="mt-1.5" rows={3} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="p-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Code</th>
              <th className="text-left px-4 py-2.5">Product</th>
              <th className="text-left px-4 py-2.5 hidden md:table-cell">Category</th>
              <th className="text-left px-4 py-2.5 hidden md:table-cell">Unit</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pager.pageItems.map((p) => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/70">
                <td className="px-4 py-3 font-mono text-slate-700">{p.product_code}</td>
                <td className="px-4 py-3">
                  <div className="text-slate-800">{p.description}</div>
                  <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
                    {p.hsn && <span data-testid={`p-hsn-chip-${p.id}`}>HSN {p.hsn}</span>}
                  </div>
                  {p.specifications && <div className="text-xs text-slate-500 mt-0.5">{p.specifications}</div>}
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs">{p.category}</span>
                </td>
                <td className="px-4 py-3 hidden md:table-cell text-slate-600">{p.unit}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => edit(p)} className="text-slate-500 hover:text-blue-600 mr-2" data-testid={`p-edit-${p.id}`}><Pencil size={14} /></button>
                  <button onClick={() => del(p.id)} className="text-slate-500 hover:text-rose-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={5} className="text-center text-sm text-slate-500 py-10">No products.</td></tr>}
          </tbody>
        </table>
        <Pager pager={pager} />
      </div>

      <Dialog open={impOpen} onOpenChange={(v) => { setImpOpen(v); if (!v) setImpPreview(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Import Products from Excel</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={downloadTemplate} className="border-slate-300 text-slate-700" data-testid="p-import-template">
                <Download size={14} className="mr-1.5" /> Download Template
              </Button>
              <label className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-300 rounded-md hover:bg-slate-50 text-slate-700 cursor-pointer">
                <Upload size={14} /> {impBusy ? "Reading…" : "Choose .xlsx file"}
                <input type="file" accept=".xlsx" className="hidden" data-testid="p-import-file"
                       onChange={(e) => { uploadPreview(e.target.files[0]); e.target.value = ""; }} />
              </label>
            </div>
            <p className="text-xs text-slate-500">Fill the template (one product per row, <b>product_code</b> is required) and upload it. Nothing is saved until you confirm the preview below.</p>

            {impPreview && (
              <div className="space-y-4" data-testid="p-import-preview">
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2.5 py-1 font-medium">{impPreview.new.length} new</span>
                  <span className="bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2.5 py-1 font-medium">{impPreview.duplicates.length} duplicate{impPreview.duplicates.length === 1 ? "" : "s"}</span>
                  {impPreview.errors.length > 0 && <span className="bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-2.5 py-1 font-medium">{impPreview.errors.length} error{impPreview.errors.length === 1 ? "" : "s"}</span>}
                </div>

                {impPreview.errors.length > 0 && (
                  <div className="border border-rose-200 bg-rose-50 rounded-md px-3 py-2 text-xs text-rose-700 space-y-0.5">
                    {impPreview.errors.map((e, i) => <div key={i}>Row {e.row}: {e.error}</div>)}
                  </div>
                )}

                {impPreview.new.length > 0 && (
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">New products</div>
                    <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-40 overflow-y-auto">
                      {impPreview.new.map((r, i) => (
                        <div key={i} className="px-3 py-1.5 text-sm text-slate-700 flex justify-between">
                          <span><span className="font-mono text-xs mr-2">{r.product_code}</span>{r.description}</span>
                          <span className="text-xs text-slate-400">{r.category}{r.price ? ` · ₹${r.price}` : ""}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {impPreview.duplicates.length > 0 && (
                  <div className="space-y-3">
                    <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Duplicates found — choose which to keep</div>
                    {impPreview.duplicates.map((d) => (
                      <div key={d.existing_id} className="border border-amber-200 bg-amber-50/50 rounded-md p-3 space-y-2" data-testid={`p-import-dup-${d.existing_id}`}>
                        <div className="text-sm font-semibold text-slate-800">{d.existing_name}</div>
                        {d.changes.length ? (
                          <table className="w-full text-xs">
                            <thead><tr className="text-slate-400 text-left"><th className="py-1 font-medium">Field</th><th className="py-1 font-medium">Current</th><th className="py-1 font-medium">In file</th></tr></thead>
                            <tbody>
                              {d.changes.map((ch) => (
                                <tr key={ch.field} className="border-t border-amber-100">
                                  <td className="py-1 text-slate-500">{ch.field.replaceAll("_", " ")}</td>
                                  <td className="py-1 text-slate-700">{ch.old || <span className="text-slate-300">—</span>}</td>
                                  <td className="py-1 font-medium text-slate-900">{ch.new}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : <p className="text-xs text-slate-500">Identical to the existing record — nothing to change.</p>}
                        {d.changes.length > 0 && (
                          <div className="flex gap-4 text-xs pt-1">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input type="radio" name={`pres-${d.existing_id}`} checked={impRes[d.existing_id] === "keep"}
                                     onChange={() => setImpRes((m) => ({ ...m, [d.existing_id]: "keep" }))} data-testid={`p-import-keep-${d.existing_id}`} />
                              Keep existing
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input type="radio" name={`pres-${d.existing_id}`} checked={impRes[d.existing_id] === "replace"}
                                     onChange={() => setImpRes((m) => ({ ...m, [d.existing_id]: "replace" }))} data-testid={`p-import-replace-${d.existing_id}`} />
                              Use values from file
                            </label>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <Button onClick={commitImport} disabled={impBusy || (impPreview.new.length === 0 && !impPreview.duplicates.some((d) => impRes[d.existing_id] === "replace"))}
                        className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="p-import-commit">
                  {impBusy ? "Importing…" : `Import ${impPreview.new.length} new${impPreview.duplicates.filter((d) => impRes[d.existing_id] === "replace").length ? ` + update ${impPreview.duplicates.filter((d) => impRes[d.existing_id] === "replace").length}` : ""}`}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, v, on, type = "text", tid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500">{label}</Label>
      <Input value={v} onChange={(e) => on(e.target.value)} className="mt-1.5" type={type} data-testid={tid} />
    </div>
  );
}
